import { currentLocale } from '~/utils';

/**
 * Represents a single field change in a history entry.
 */
export type FieldChange = {
  field: string;
  oldValue: string | null;
  newValue: string | null;
};

/**
 * Labels used when extracting field changes from complex history entries.
 */
export type FieldChangeLabels = {
  itemAdded: string;
  itemRemoved: string;
  countLabel: string;
};

/**
 * Base shape required for preparing history items for the Timeline component.
 * Both contract and callOff history entries must satisfy this type.
 */
type HistoryEntryBase = {
  createdAt: string;
  versionNumber: number;
};

/**
 * Format an ISO datetime string into separate date and time parts.
 */
export function formatIsoDateTimeParts(
  isoDateTime: string | null | undefined,
): {
  date: string;
  time: string;
} {
  if (!isoDateTime) {
    return {
      date: '—',
      time: '',
    };
  }

  const date = new Date(isoDateTime);
  const locale = currentLocale();

  // Handle invalid dates
  if (Number.isNaN(date.getTime())) {
    return {
      date: '—',
      time: '',
    };
  }

  return {
    date: new Intl.DateTimeFormat(locale, {
      dateStyle: 'medium',
    })
      .format(date)
      .replace(/^0/, ''), // Strip leading zero - this just makes it less readable
    time: new Intl.DateTimeFormat(locale, {
      timeStyle: 'short',
    })
      .format(date)
      .replace(/^0/, ''), // Strip leading zero - this just makes it less readable
  };
}

/**
 * Minimal shape of an attachment change entry as returned by the API.
 */
type AttachmentChange = {
  name: string;
  action: 'added' | 'deleted';
};

/**
 * Converts a list of attachment changes into `FieldChange` objects suitable
 * for display in `FieldChangesSection`.
 *
 * - added   → { field: attachmentLabel, oldValue: null,        newValue: filename    }
 * - deleted → { field: attachmentLabel, oldValue: filename,    newValue: removedLabel }
 */
export function extractAttachmentChanges(
  attachmentChanges: AttachmentChange[] | null | undefined,
  attachmentLabel: string,
  removedLabel: string,
): FieldChange[] {
  if (!attachmentChanges?.length) {
    return [];
  }

  return attachmentChanges.map((change) => ({
    field: attachmentLabel,
    newValue: change.action === 'added' ? change.name : removedLabel,
    oldValue: change.action === 'deleted' ? change.name : null,
  }));
}

/**
 * Prepares raw history entries for the Timeline component.
 * Sorts by versionNumber descending (newest first) and adds a unique `id` field.
 *
 * Generic over any history entry type that has `createdAt` and `versionNumber`.
 */
export function prepareHistoryItems<T extends HistoryEntryBase>(
  history: T[],
): Array<T & { id: string }> {
  if (!history?.length) {
    return [];
  }

  return history
    .slice()
    .sort((a, b) => b.versionNumber - a.versionNumber)
    .map((entry, index) => ({
      ...entry,
      id: `${entry.createdAt}-${entry.versionNumber}-${index}`,
    }));
}
